import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Text, View, ScrollView, Pressable, RefreshControl } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SheetModal from '@/components/General/SheetModal';
import SearchBox from '@/components/General/inputs/SearchBox';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import { useRouter, useLocalSearchParams, useNavigation, usePathname } from 'expo-router';
import usePlatform from '@/utils/hooks/usePlatform';
import { applyAssignmentTemplate, deleteTaskAssignmentTemplate, updateAssignees, getAssigneesNeeded, getConflictingTaskTitles } from '@/data/tasks';
import SortableList from '@/components/General/SortableList';
import Toast from 'react-native-toast-message';
import { showAlert } from '@/utils/showAlert';
import Avatar from '@/components/People/Avatar';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SchedulePDFExport from '@/components/Tasks/SchedulePDFExport';
import AutoScheduleModal from '@/components/Tasks/AutoScheduleModal';
import { useAutoScheduleAccess } from '@/components/Tasks/autoScheduleModel';
import { Tooltip } from '@/components/General/Tooltip';
import { buildScheduleGridData } from '@/utils/buildScheduleGridData';
import useTeamFilterStore from '@/data/store/useTeamFilterStore';

function ManageTemplatesModal({ isOpen, onClose }) {
    const queryClient = useQueryClient();
    const router = useRouter();
    const { data: templates, isFetching } = Query.taskAssignmentTemplates.all();
    const { data: loggedInUser } = Query.people.loggedInUser();

    const deleteMutation = useMutation({
        mutationFn: deleteTaskAssignmentTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskAssignmentTemplates.all() });
        },
    });

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            title="Manage Templates"
        >
            <View style={styles.manageModalContent}>
                {isFetching ? (
                    <EmptyListPlaceholder message="Loading templates..." />
                ) : templates?.length === 0 ? (
                    <EmptyListPlaceholder message="No templates yet. Create one from a team's volunteer tasks." />
                ) : (
                    <ScrollView contentContainerStyle={styles.manageModalList} keyboardShouldPersistTaps="handled"
>
                        {templates?.map((template) => (
                            <View key={template.id} style={styles.manageCard}>
                                <View style={styles.manageCardHeader}>
                                    <View style={styles.manageCardIcon}>
                                        <Unicon name="copy-outline" size={18} colorVariant="primary" />
                                    </View>
                                    <View style={styles.manageCardInfo}>
                                        <Text style={styles.manageCardTitle}>{template.name}</Text>
                                        <Text style={styles.manageCardSubtitle}>
                                            {template.VolunteerTeam?.name || 'Unknown Team'} · {template.Members?.length || 0} assignments
                                        </Text>
                                    </View>
                                    {loggedInUser?.can?.("Update Tasks") && (
                                        <DropdownMenu
                                            options={[
                                                {
                                                    name: "Edit",
                                                    icon: dropdownIcons.edit,
                                                    onSelect: () => {
                                                    onClose();
                                                        router.push({ pathname: '/tasks/templates/create', params: { id: template.id } });
                                                    },
                                                },
                                                {
                                                    name: "Delete",
                                                    icon: dropdownIcons.delete,
                                                    destructive: true,
                                                    onSelect: () => {
                                                        showAlert({
                                                            title: "Delete Template",
                                                            subtitle: `Delete "${template.name}"?`,
                                                            isDestructive: true,
                                                            confirmText: "Delete",
                                                            onConfirm: () => deleteMutation.mutate(template.id),
                                                        });
                                                    },
                                                },
                                            ]}
                                        />
                                    )}
                                </View>
                            </View>
                        ))}
                    </ScrollView>
                )}
            </View>
        </SheetModal>
    );
}

export default function TaskScheduleScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const pathname = usePathname();
    const { eventID } = useLocalSearchParams();
    const { isPortrait, isLandscape } = usePlatform();

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [selectedTemplateInstance, setSelectedTemplateInstance] = useState(null);
    const [manageTemplatesModalOpen, setManageTemplatesModalOpen] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pendingAssigneeUpdate, setPendingAssigneeUpdate] = useState({});
    const [openAssigneePositionsModal, setOpenAssigneePositionsModal] = useState(false);
    const [autoScheduleTeam, setAutoScheduleTeam] = useState(null); // {teamID, teamName}
    const { allowed: canAutoSchedule, reason: autoScheduleReason } = useAutoScheduleAccess();

    const { data: loggedInUser } = Query.people.loggedInUser();

    const { data: preselectedEvent } = Query.events.byId({
        id: eventID,
        options: { enabled: !!eventID && !selectedEvent },
    });

    useEffect(() => {
        if (preselectedEvent && !selectedEvent) {
            setSelectedEvent(preselectedEvent);
        }
    }, [preselectedEvent, selectedEvent]);

    // Persisted team filter — hydrate once on mount
    const selectedTeamID = useTeamFilterStore((s) => s.selectedTeamID);
    useEffect(() => {
        useTeamFilterStore.getState().hydrate();
    }, []);

    useEffect(() => {
        navigation.setOptions({
            headerShown: true,
            gestureEnabled: false,
            headerRight: () => (
                isPortrait ? (
                    <DropdownMenu
                        options={[
                            {
                                name: "Export Schedule",
                                icon: dropdownIcons.export,
                                onSelect: () => setExportModalOpen(true),
                            },
                            {
                                name: "Manage Templates",
                                onSelect: () => setManageTemplatesModalOpen(true),
                            },
                        ]}
                    />
                ) : null
            ),
        });
    }, [navigation, isPortrait]);

    const { data: templates } = Query.taskAssignmentTemplates.all();
    const { data: organization } = Query.organization.byId();

    const {
        data: instances = [],
        isLoading: isLoadingInstances,
        refetch: refetchInstances,
    } = Query.eventInstances.upcomingVolunteerTasks({
        eventID: selectedEvent?.id,
        options: { enabled: !!selectedEvent?.id },
    });

    const handleEventSelect = useCallback((event) => {
        setSelectedEvent(event);
        if (event?.id) {
            router.setParams({ eventID: String(event.id) });
        }
    }, [router]);

    // Build the grid data
    const gridData = useMemo(() => {
        return buildScheduleGridData(instances);
    }, [instances]);

    // Derive the selected team filter object from the persisted team ID.
    // Falls back to null (All Teams) when the ID is absent or not in this event's teams.
    const selectedTeamFilter = useMemo(() => {
        if (!selectedTeamID) return null;
        const match = gridData.find(g => g.tasks.some(t => t.volunteerTeamID === selectedTeamID));
        return match ? { id: selectedTeamID, name: match.teamName } : null;
    }, [gridData, selectedTeamID]);

    // Check if the current user can assign for a team (admin or VolunteerTeamAssigner)
    const canAssignForTeam = useCallback((team) => {
        if (loggedInUser?.isAdmin) return true;
        return team?.assigners?.some(a => a.personID === loggedInUser?.id);
    }, [loggedInUser]);

    // Filter by team and search
    const filteredGridData = useMemo(() => {
         let data = gridData.filter(g => {
            const canAssign = canAssignForTeam(g);
            return canAssign;
        });
        if (selectedTeamFilter) {
            data = data.filter(g => (g.volunteerTeamID ?? null) === selectedTeamFilter.id);
        }
        if (searchText) {
            const lower = searchText.toLowerCase();
            data = data.map(g => ({
                ...g,
                tasks: g.tasks.filter(t => t.task.title.toLowerCase().includes(lower)),
            })).filter(g => g.tasks.length > 0);
        }
        return data;
    }, [gridData, selectedTeamFilter, searchText, canAssignForTeam]);

    // Collect unique task IDs for batch recent-assignees fetch
    const allTaskIDs = useMemo(() => {
        const ids = new Set();
        gridData.forEach(team => team.tasks.forEach(t => ids.add(t.task.id)));
        return Array.from(ids);
    }, [gridData]);

    const { data: recentAssigneesMap } = Query.tasks.recentAssigneesBatch({
        taskIDs: allTaskIDs,
        options: { enabled: allTaskIDs.length > 0 },
    });

    // Template application mutation
    const applyTemplateMutation = useMutation({
        mutationFn: applyAssignmentTemplate,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(selectedEvent?.id) });
            const count = data?.length || 0;
            Toast.show({ type: 'success', text2: `Template applied to ${count} task${count !== 1 ? 's' : ''}` });
            setTemplateModalOpen(false);
        },
        onError: (error) => {
            const msg = error?.message?.includes('Permission denied')
                ? 'You do not have permission to apply templates for this team'
                : 'Failed to apply template. Please try again.';
            Toast.show({ type: 'error', text2: msg });
        },
    });

    const handleApplyTemplate = useCallback((template) => {
        if (!selectedTemplateInstance) return;

        const instanceData = instances.find(i => i.id === selectedTemplateInstance.id);
        const teamTasks = instanceData?.TaskInstances?.filter(ti =>
            ti.VolunteerTeam?.id === template.volunteerTeamID &&
            (ti.Assignees?.length > 0)
        );

        if (teamTasks?.length > 0) {
            showAlert({
                title: 'Some tasks already assigned',
                subtitle: `${teamTasks.length} task${teamTasks.length !== 1 ? 's' : ''} already have assignees. The template will only fill empty cells.`,
                confirmText: 'Apply Anyway',
                onConfirm: () => {
                    applyTemplateMutation.mutate({
                        templateID: template.id,
                        eventInstanceID: selectedTemplateInstance.id,
                        currentUser: loggedInUser?.id,
                    });
                },
            });
        } else {
            applyTemplateMutation.mutate({
                templateID: template.id,
                eventInstanceID: selectedTemplateInstance.id,
                currentUser: loggedInUser?.id,
            });
        }
    }, [selectedTemplateInstance, instances, applyTemplateMutation]);

    // Instant assign mutation (mirrors TaskCard assignee section)
    const updateAssigneesMutation = useMutation({
        mutationFn: updateAssignees,
        onSuccess: (data) => {
            data.forEach(a => {
                queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(a.personID) });
            });
            if (selectedEvent?.id) {
                queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(selectedEvent.id) });
            }
        },
    });

    const handleAssigneeChange = useCallback((cellData, selectedItems) => {
        const taskInstance = cellData?.taskInstance;
        const pendingUpdate = {
            taskInstanceID: cellData?.taskInstanceID,
            existingAssignees: cellData?.assignees,
            newAssignees: selectedItems,
            taskInstance: taskInstance,
            currentUser: loggedInUser?.id,
        };

        const assigneesNeeded = getAssigneesNeeded(taskInstance);
        if (selectedItems.length > assigneesNeeded) {
            setPendingAssigneeUpdate(pendingUpdate);
            setTimeout(() => setOpenAssigneePositionsModal(true), 500);
        } else {
            updateAssigneesMutation.mutate(pendingUpdate);
        }
    }, [loggedInUser, updateAssigneesMutation]);

    // Date formatter
    const formatInstanceDate = useCallback((startTime) => {
        const date = new Date(startTime);
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }, []);

    const formatInstanceDay = useCallback((startTime) => {
        const date = new Date(startTime);
        return date.toLocaleDateString(undefined, { weekday: 'short' });
    }, []);

    if (!selectedEvent) {
        return (
            <View style={styles.container}>
                <View style={styles.emptyState}>
                    <Unicon name="calendar-outline" size={48} colorVariant="textSecondary" />
                    <Text style={styles.emptyTitle}>Select an Event</Text>
                    <Text style={styles.emptySubtitle}>Choose an event to see its task schedule</Text>
                    <View style={styles.eventSelector}>
                        <SheetSelector
                            type="events"
                            title="Select Event"
                            value={selectedEvent}
                            onChange={handleEventSelect}
                        >
                            <SolidButton iconName="calendar" text="Choose Event" />
                        </SheetSelector>
                    </View>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerTop}>
                    <SheetSelector
                        type="events"
                        title="Select Event"
                        value={selectedEvent}
                        onChange={handleEventSelect}
                    >
                        <View style={styles.eventChip}>
                            <Unicon name="calendar" size={16} colorVariant="primary" />
                            <Text style={styles.eventChipText}>{selectedEvent.name}</Text>
                            <Unicon name="chevron-down" size={14} colorVariant="textSecondary" />
                        </View>
                    </SheetSelector>

                    {isLandscape && (
                        <View style={styles.headerActions}>
                            <SolidButton
                                text="Export Schedule"
                                iconName="download-outline"
                                iconBefore
                                size="sm"
                                variant="muted"
                                type="primary"
                                onPress={() => setExportModalOpen(true)}
                            />
                            <SolidButton
                                text="Manage Templates"
                                iconName="clipboard-text-multiple"
                                iconLibrary='material'
                                iconBefore
                                size="sm"
                                variant="muted"
                                type="primary"
                                onPress={() => setManageTemplatesModalOpen(true)}
                            />
                        </View>
                    )}
                </View>

                <View style={styles.filtersRow}>
                    <View style={styles.searchBox}>
                        <SearchBox searchText={searchText} onChangeText={setSearchText} placeholder="Search tasks..." />
                    </View>
                    <SheetSelector
                        type="taskTeams"
                        title="Filter by Team"
                        value={selectedTeamFilter}
                        onChange={(team) => useTeamFilterStore.getState().setTeamID(team?.id ?? null)}
                    >
                        <View style={[styles.teamFilterChip, selectedTeamFilter && styles.teamFilterChipActive]}>
                            <Unicon name="people-outline" size={14} colorVariant={selectedTeamFilter ? "primary" : "textSecondary"} />
                            <Text style={[styles.teamFilterText, selectedTeamFilter && styles.teamFilterTextActive]}>
                                {selectedTeamFilter?.name || 'All Teams'}
                            </Text>
                        </View>
                    </SheetSelector>
                </View>
            </View>

            {/* Schedule Grid */}
            {isLoadingInstances ? (
                <View style={styles.loadingContainer}>
                    <Text style={styles.loadingText}>Loading schedule...</Text>
                </View>
            ) : filteredGridData.length === 0 ? (
                <EmptyListPlaceholder message="No volunteer tasks found for this event" />
            ) : (
                <ScrollView
                    style={styles.scrollView}
                    keyboardShouldPersistTaps="handled"
                    refreshControl={<RefreshControl refreshing={isLoadingInstances} onRefresh={refetchInstances} />}
                >
                    <View style={styles.gridWrapper}>
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={true}
                            keyboardShouldPersistTaps="handled"
                        >
                            <View>
                                {/* Column Headers (Dates) */}
                                <View style={styles.gridHeaderRow}>
                                    <View style={styles.taskNameHeader}>
                                        <Text style={styles.headerText}>Task / Team</Text>
                                    </View>
                                    <View style={styles.datesRow}>
                                        {instances.map(inst => (
                                            <View key={inst.id} style={styles.dateCell}>
                                                <Text style={styles.dateDay}>{formatInstanceDay(inst.startTime)}</Text>
                                                <Text style={styles.dateText}>{formatInstanceDate(inst.startTime)}</Text>
                                            </View>
                                        ))}
                                    </View>
                                </View>

                                {/* Team Sections */}
                                {filteredGridData.map((teamGroup) => (
                                    <View key={teamGroup.volunteerTeamID ?? teamGroup.teamName}>
                                        {(() => {
                                            const teamID = teamGroup.tasks[0]?.volunteerTeamID;
                                            const teamTemplates = templates?.filter(t => t.volunteerTeamID === teamID) || [];
                                            const hasLinkedRole = !!teamGroup.teamRoleID;
                                            const isTeamAssigner = canAssignForTeam(teamGroup);
                                            const autoScheduleDisabled = !canAutoSchedule || !hasLinkedRole || !isTeamAssigner;
                                            const autoScheduleDisabledReason = !canAutoSchedule
                                                ? autoScheduleReason
                                                : !isTeamAssigner
                                                    ? 'Auto-Schedule needs you to be an assigner for this team.'
                                                    : 'Auto-Schedule needs a member role linked to this team. Use Edit Team to link one.';
                                            const canEditTeam = isTeamAssigner && !!teamID && !!loggedInUser?.can?.('Update Tasks');
                                            const showAutoSchedule = !!teamID;
                                            return (
                                                <>
                                                    <View style={styles.teamHeader}>
                                                        <View style={styles.teamHeaderLeft}>
                                                            {showAutoSchedule && (
                                                                <Tooltip
                                                                    testID={`TeamAutoSchedule-${teamID}`}
                                                                    title={autoScheduleDisabled ? autoScheduleDisabledReason : undefined}
                                                                    appearOnTouch={autoScheduleDisabled}
                                                                    onPress={autoScheduleDisabled ? null : () => setAutoScheduleTeam({ teamID, teamName: teamGroup.teamName })}
                                                                    accessibilityLabel={autoScheduleDisabled
                                                                        ? `Auto-Schedule ${teamGroup.teamName} team: ${autoScheduleDisabledReason || 'unavailable'}`
                                                                        : `Auto-Schedule ${teamGroup.teamName} team`}
                                                                    style={styles.autoSchedulePressable}
                                                                >
                                                                    <View style={styles.autoScheduleContent} pointerEvents="none">
                                                                        <Unicon name="sparkles-outline" size={13} colorVariant={autoScheduleDisabled ? 'textTertiary' : 'tasks'} />
                                                                        <Text style={[styles.autoScheduleButton, autoScheduleDisabled && styles.autoScheduleButtonDisabled]}>Auto-Schedule</Text>
                                                                    </View>
                                                                </Tooltip>
                                                            )}
                                                            {canEditTeam && (
                                                                <Pressable
                                                                    testID={`TeamEdit-${teamID}`}
                                                                    accessibilityRole="button"
                                                                    accessibilityLabel={`Edit ${teamGroup.teamName} team`}
                                                                    onPress={() => router.push({ pathname: '/tasks/teams/create', params: { id: teamID } })}
                                                                    style={styles.editTeamButton}
                                                                >
                                                                    <Unicon name="create-outline" size={13} colorVariant="textSecondary" />
                                                                    <Text style={styles.editTeamText}>Edit Team</Text>
                                                                </Pressable>
                                                            )}
                                                            {(showAutoSchedule || canEditTeam) && <View style={styles.teamHeaderSeparator} />}
                                                            <Text style={styles.teamHeaderText}>{teamGroup.teamName}</Text>
                                                        </View>
                                                        <Text style={styles.teamTaskCount}>{teamGroup.tasks.length} task{teamGroup.tasks.length !== 1 ? 's' : ''}</Text>
                                                    </View>
                                                    {teamTemplates.length > 0 && (
                                                        <View style={styles.applyTemplateRow}>
                                                            <View style={styles.applyTemplateLabelCell}>
                                                                <Text style={styles.applyTemplateLabel}>Templates</Text>
                                                            </View>
                                                            <View style={styles.datesRow}>
                                                                {instances.map(inst => (
                                                                    <View key={inst.id} style={styles.applyTemplateCell}>
                                                                        <SolidButton
                                                                            text="Apply"
                                                                            iconName="clipboard-text-multiple"
                                                                            iconLibrary='material'
                                                                            iconBefore
                                                                            size="sm"
                                                                            variant="muted"
                                                                            type="primary"
                                                                            onPress={() => {
                                                                                setSelectedTemplateInstance({ id: inst.id, teamID });
                                                                                setTemplateModalOpen(true);
                                                                            }}
                                                                        />
                                                                    </View>
                                                                ))}
                                                            </View>
                                                        </View>
                                                    )}
                                                </>
                                            );
                                        })()}

                                        {teamGroup.tasks.map((taskData) => (
                                            <View key={taskData.task.id} style={styles.taskRow}>
                                                <Pressable
                                                    style={styles.taskNameCell}
                                                    onPress={() => router.push(`/tasks/${taskData.task.id}`)}
                                                >
                                                    <Text style={styles.taskNameText} numberOfLines={2}>{taskData.task.title}</Text>
                                                </Pressable>
                                                <View style={styles.assigneeCells}>
                                                    {instances.map(inst => {
                                                        const cellData = taskData.instances.get(inst.id);
                                                        const assignees = cellData?.assignees || [];
                                                        const hasConflict = assignees.some(a => a.conflicts?.length > 0);
                                                        const taskInstance = cellData?.taskInstance;

                                                        if (!cellData) {
                                                            return (
                                                                <View key={inst.id} style={styles.assigneeCell}>
                                                                    <View style={styles.noTaskCell}>
                                                                        <Text style={styles.noTaskText}>-</Text>
                                                                    </View>
                                                                </View>
                                                            );
                                                        }

                                                        if (cellData.multipleInstances) {
                                                            // One cell holds >1 task instance — edit each via
                                                            // the task detail page instead of editing an
                                                            // arbitrary instance here.
                                                            return (
                                                                <View key={inst.id} style={styles.assigneeCellWrapper}>
                                                                    <View style={[styles.assigneeCellInner, styles.multipleInstanceCell]}>
                                                                        <Unicon name="layers-outline" size={13} colorVariant="warning" />
                                                                        <Text style={styles.multipleInstanceText}>{cellData.taskInstanceIDs?.length || 2} tasks</Text>
                                                                    </View>
                                                                </View>
                                                            );
                                                        }

                                                        return (
                                                            <View key={inst.id} style={styles.assigneeCellWrapper}>
                                                                <View style={styles.assigneeCellInner}>
                                                                <SheetSelector
                                                                    title={taskData.task.title + " - Assignees"}
                                                                    multiple
                                                                    forceModal
                                                                    fromScreen={pathname}
                                                                    addScreenName={loggedInUser?.can?.("Create People") ? "/people/create" : null}
                                                                    type="people"
                                                                    name="Assignees"
                                                                    id={cellData.taskInstanceID}
                                                                    value={assignees}
                                                                    recents={recentAssigneesMap?.[taskData.task.id]}
                                                                    errorFn={(item) => {
                                                                        const errors = [];
                                                                        if (item.BlackoutPeriods?.length > 0) {
                                                                            const isInBlackoutPeriod = item.BlackoutPeriods?.some(b => {
                                                                                const due = new Date(taskInstance?.dueDate);
                                                                                return due >= new Date(b.startDate) && due <= new Date(b.endDate);
                                                                            });
                                                                            if (isInBlackoutPeriod) errors.push("This date is blacked out");
                                                                        }
                                                                        const conflicts = getConflictingTaskTitles(taskInstance, item.id);
                                                                        if (conflicts.length > 0) errors.push(`Scheduling conflict with ${conflicts.join(', ')}`);
                                                                        return errors;
                                                                    }}
                                                                    onChange={(selectedItems) => handleAssigneeChange(cellData, selectedItems)}
                                                                >
                                                                    <View style={styles.assigneeCellTrigger} pointerEvents="none">
                                                                        {assignees.length > 0 ? (<>
                                                                            <View style={styles.assigneeContent} pointerEvents="none">
                                                                                {assignees.slice(0, 3).map((a, idx) => (
                                                                                    <View key={idx} style={styles.avatar(idx === 0)} pointerEvents="none">
                                                                                        <Avatar profile={a.Person} size={40} disableBorder />
                                                                                    </View>
                                                                                ))}
                                                                            </View>
                                                                            {assignees.length > 3 && (
                                                                                <View pointerEvents="none">
                                                                                    <Text style={styles.moreAssigneesText}>+{assignees.length - 3} More...</Text>
                                                                                </View>
                                                                            )}
                                                                            {hasConflict && (
                                                                                <View style={styles.conflictBadge} pointerEvents="none">
                                                                                    <Text style={styles.conflictText}>!</Text>
                                                                                </View>
                                                                            )}
                                                                            </>
                                                                        ) : (
                                                                            <View style={styles.unassignedCell} pointerEvents="none">
                                                                                <Unicon name="add-outline" size={16} colorVariant="textSecondary" />
                                                                                <Text style={styles.unassignedText}>Assign</Text>
                                                                            </View>
                                                                        )}
                                                                    </View>
                                                                </SheetSelector>
                                                                </View>
                                                            </View>
                                                        );
                                                    })}
                                                </View>
                                            </View>
                                        ))}
                                    </View>
                                ))}
                            </View>
                        </ScrollView>
                    </View>
                </ScrollView>
            )}

            {/* Template Selection Modal */}
            <SheetModal
                isOpen={templateModalOpen}
                onClose={() => setTemplateModalOpen(false)}
                title="Choose a Template"
            >
                <View style={styles.templateList}>
                    {(templates?.filter(t => !selectedTemplateInstance?.teamID || t.volunteerTeamID === selectedTemplateInstance.teamID) || []).map(template => (
                        <Pressable
                            key={template.id}
                            style={styles.templateItem}
                            onPress={() => handleApplyTemplate(template)}
                        >
                            <Text style={styles.templateName}>{template.name}</Text>
                            <Text style={styles.templateMeta}>{template.Members?.length || 0} assignments</Text>
                        </Pressable>
                    ))}
                    {(!templates || templates.length === 0) && (
                        <Text style={styles.noTemplatesText}>No templates available yet.</Text>
                    )}
                </View>
            </SheetModal>

            {/* Manage Templates Modal */}
            <ManageTemplatesModal
                isOpen={manageTemplatesModalOpen}
                onClose={() => setManageTemplatesModalOpen(false)}
            />
            <AutoScheduleModal
                isOpen={!!autoScheduleTeam}
                onClose={() => setAutoScheduleTeam(null)}
                teamIDs={autoScheduleTeam?.teamID ? [autoScheduleTeam.teamID] : []}
                teamNames={autoScheduleTeam?.teamName ? [autoScheduleTeam.teamName] : []}
                eventID={selectedEvent?.id ?? null}
                eventName={selectedEvent?.name ?? null}
                source="team-schedule"
            />
            <SchedulePDFExport
                isOpen={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                defaultEvent={selectedEvent}
                organization={organization}
            />

            {/* Assignee Positions Modal (when more assignees than needed) */}
            <SortableList
                helpText="Reorder assignees to change their priority. Anyone labeled 'Primary' will be assigned first. If any of the 'Primary' assignees decline, the next 'Backup' will be notified and assigned."
                isOpen={openAssigneePositionsModal}
                close={() => setOpenAssigneePositionsModal(false)}
                onConfirm={(data) => {
                    const updatedAssignees = data.map((assignee, i) => ({ ...assignee, position: i }));
                    updateAssigneesMutation.mutate({ ...pendingAssigneeUpdate, newAssignees: updatedAssignees });
                }}
                items={pendingAssigneeUpdate?.newAssignees?.slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) || []}
                title="Order Assignees"
                itemContent={(item) => <Text style={styles.assigneeText}>{item?.firstName + " " + item?.lastName}</Text>}
                itemHeight={50}
                labels={pendingAssigneeUpdate?.newAssignees?.map((assignee, i) => {
                    const needed = getAssigneesNeeded(pendingAssigneeUpdate?.taskInstance);
                    return i < needed
                        ? <Text key={i} style={styles.assigneeChipText("success")}>Primary</Text>
                        : <Text key={i} style={styles.assigneeChipText("warning")}>Backup</Text>;
                })}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts }) => ({
        container: { flex: 1, backgroundColor: 'transparent' },
        scrollView: { flex: 1 },

        header: {
            paddingHorizontal: 16,
            paddingTop: 12,
            paddingBottom: 8,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
            // backgroundColor: colors.surface,
        },
        headerTop: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
        },
        headerActions: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
        },
        eventChip: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            backgroundColor: colors.primaryContainer,
            paddingHorizontal: 12,
            paddingVertical: 8,
            borderRadius: 20,
        },
        eventChipText: {
            fontFamily: fonts.heading,
            fontSize: 14,
            color: colors.primary,
        },
        templateBtn: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingHorizontal: 12,
            paddingVertical: 8,
        },
        templateBtnText: {
            fontSize: 14,
            color: colors.primary,
            fontFamily: fonts.heading,
        },
        filtersRow: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
        },
        searchBox: {
            flex: 1,
        },
        teamFilterChip: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingHorizontal: 10,
            paddingVertical: 6,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: colors.border
        },
        teamFilterChipActive: {
            borderColor: colors.primary,
            backgroundColor: colors.primaryContainer,
        },
        teamFilterText: {
            fontSize: 13,
            color: colors.textSecondary,
        },
        teamFilterTextActive: {
            color: colors.primary,
            fontFamily: fonts.bold,
        },

        gridWrapper: {
            paddingBottom: 40,
        },
        gridHeaderRow: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 16,
            paddingVertical: 10,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
            backgroundColor: colors.surface,
        },
        taskNameHeader: {
            width: 140,
            paddingRight: 8,
        },
        headerText: {
            fontSize: 11,
            fontFamily: fonts.bold,
            color: colors.textSecondary,
            textTransform: 'uppercase',
        },
        datesRow: {
            flexDirection: 'row',
        },
        dateCell: {
            width: 90,
            alignItems: 'center',
            justifyContent: 'center',
        },
        dateDay: {
            fontSize: 10,
            color: colors.textSecondary,
            textTransform: 'uppercase',
        },
        dateText: {
            fontSize: 12,
            fontFamily: fonts.bold,
            color: colors.text,
        },

        teamHeader: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: 16,
            paddingVertical: 10,
            backgroundColor: colors.background,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
        },
        teamHeaderLeft: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            flexShrink: 1,
        },
        teamHeaderSeparator: {
            width: 1,
            height: 16,
            backgroundColor: colors.border,
        },
        teamHeaderText: {
            fontSize: 13,
            fontFamily: fonts.bold,
            color: colors.textSecondary,
            textTransform: 'uppercase',
            flexShrink: 1,
        },
        teamTaskCount: {
            fontSize: 12,
            color: colors.textSecondary,
        },
        autoSchedulePressable: {
            _web: {
                cursor: 'pointer',
            },
        },
        autoScheduleContent: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
        },
        autoScheduleButton: {
            fontSize: 12,
            fontFamily: fonts.bold,
            color: colors.tasks,
        },
        autoScheduleButtonDisabled: {
            color: colors.textTertiary,
        },
        editTeamButton: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            _web: {
                cursor: 'pointer',
            },
        },
        editTeamText: {
            fontSize: 12,
            fontFamily: fonts.bold,
            color: colors.textSecondary,
        },
        multipleInstanceCell: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            width: 90,
            height: 55,
            borderRadius: 8,
            backgroundColor: colors.surfaceVariant,
        },
        multipleInstanceText: {
            fontSize: 10,
            color: colors.warning,
            fontFamily: fonts.bold,
        },

        taskRow: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 16,
            paddingVertical: 8,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
            backgroundColor: colors.surface,
        },
        taskNameCell: {
            width: 140,
            paddingRight: 8,
            _web: {
                cursor: 'pointer',
            },
        },
        taskNameText: {
            fontSize: 13,
            color: colors.text,
            fontFamily: fonts.heading,
            _web: {
                transition: 'color 0.15s ease',
                _hover: {
                    color: colors.primary,
                },
            },
        },
        assigneeCells: {
            flexDirection: 'row',
        },
        assigneeCell: {
            width: 90,
            height: 55,
            alignItems: 'center',
            justifyContent: 'center',
            borderLeftWidth: 1,
            borderLeftColor: colors.border,
            _web: {
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                _hover: {
                    backgroundColor: colors.hover,
                },
            },
        },
        assigneeCellWrapper: {
            width: 90,
            height: 55,
            alignItems: 'center',
            justifyContent: 'center',
            borderLeftWidth: 1,
            borderLeftColor: colors.border,
            _web: {
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                _hover: {
                    backgroundColor: colors.hover,
                },
            },
        },
        assigneeCellInner: {
            marginVertical: -8,
            alignItems: 'center',
            justifyContent: 'center',
        },
        assigneeCellTrigger: {
            width: 90,
            height: 55,
            alignItems: 'center',
            justifyContent: 'center',
        },
        assigneeContent: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
        },
        moreAssigneesText: {
            color: colors.textSecondary,
            fontStyle: 'italic',
            fontSize: 12,
            marginTop:4
        },
        avatar: (isFirst) => ({
            marginLeft: isFirst ? 0 : -12,
        }),
        conflictBadge: {
            width: 14,
            height: 14,
            borderRadius: 7,
            backgroundColor: colors.error,
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 2,
            position: 'absolute',
            top: 0,
            right: 4,
        },
        conflictText: {
            color: '#fff',
            fontSize: 9,
            fontFamily: fonts.bold,
        },
        unassignedCell: {
            alignItems: 'center',
            justifyContent: 'center',
        },
        unassignedText: {
            fontSize: 10,
            color: colors.textSecondary,
        },
        noTaskCell: {
            alignItems: 'center',
            justifyContent: 'center',
        },
        noTaskText: {
            fontSize: 14,
            color: colors.textSecondary,
        },

        applyTemplateRow: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 16,
            paddingVertical: 4,
            backgroundColor: colors.surface,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
        },
        applyTemplateLabelCell: {
            width: 140,
            paddingRight: 8,
            justifyContent: 'center',
        },
        applyTemplateLabel: {
            fontSize: 11,
            fontFamily: fonts.bold,
            color: colors.textSecondary,
            textTransform: 'uppercase',
        },
        applyTemplateCell: {
            width: 90,
            alignItems: 'center',
            justifyContent: 'center',
        },
        applyTemplateChipSmall: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 12,
            backgroundColor: colors.primaryContainer,
            _web: {
                transition: 'all 0.15s ease',
                cursor: 'pointer',
                _hover: {
                    backgroundColor: colors.primary,
                },
            },
        },
        applyTemplateSmallText: {
            fontSize: 10,
            color: colors.primary,
            fontFamily: fonts.heading,
            _web: {
                transition: 'color 0.15s ease',
                _hover: {
                    color: colors.onPrimary,
                },
            },
        },
        emptyState: {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: 40,
            gap: 12,
        },
        emptyTitle: {
            fontSize: 18,
            fontFamily: fonts.heading,
            color: colors.text,
        },
        emptySubtitle: {
            fontSize: 14,
            color: colors.textSecondary,
            textAlign: 'center',
        },
        eventSelector: {
            marginTop: 16,
        },

        loadingContainer: {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
        },
        loadingText: {
            fontSize: 14,
            color: colors.textSecondary,
        },

        templateList: {
            padding: 16,
            gap: 8,
        },
        templateItem: {
            padding: 14,
            borderRadius: 10,
            backgroundColor: colors.surface,
            _web:{
                transition: 'background-color 0.2s',
                _hover:{
                    backgroundColor: colors.surfaceVariant,
                }
            }
        },
        templateName: {
            fontSize: 15,
            fontFamily: fonts.heading,
            color: colors.text,
        },
        templateMeta: {
            fontSize: 12,
            color: colors.textSecondary,
            marginTop: 2,
        },
        noTemplatesText: {
            fontSize: 14,
            color: colors.textSecondary,
            textAlign: 'center',
            padding: 20,
        },

        // Manage Templates Modal
        manageModalContent: {
            flex: 1,
            padding: 16,
        },
        manageModalList: {
            gap: 12,
            paddingBottom: 40,
        },
        manageCard: {
            backgroundColor: colors.surface,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: colors.border,
            padding: 14,
        },
        manageCardHeader: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
        },
        manageCardIcon: {
            width: 40,
            height: 40,
            borderRadius: 10,
            backgroundColor: colors.primaryContainer,
            alignItems: 'center',
            justifyContent: 'center',
        },
        manageCardInfo: {
            flex: 1,
        },
        manageCardTitle: {
            fontSize: 15,
            fontFamily: fonts.heading,
            color: colors.text,
        },
        manageCardSubtitle: {
            fontSize: 12,
            color: colors.textSecondary,
            marginTop: 2,
        },
        assigneeText: {
            color: colors.text,
            fontSize: 15,
            paddingLeft: 10,
            fontFamily: fonts.heading,
        },
        assigneeChipText: (colorVariant) => ({
            fontSize: 11,
            fontFamily: fonts.bold,
            color: colors[colorVariant],
        }),
    }));
